import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Tab from '../../components/common/Tab';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useMemberEndorsementApi } from '../../hooks/useMemberEndorsementApi';
import { useDedicatedStatusApi } from '../../hooks/useDedicatedStatusApi';
import { useActivityApi } from '../../hooks/useActivityApi';
import { useMarksmanGradingApi } from '../../hooks/useMarksmanGradingApi';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useRplApi } from '../../hooks/useRplApi';
import { useCompetitionEntryApi } from '../../hooks/useCompetitionEntryApi';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import { MemberDTO, MemberEndorsementDTO, ActivityDTO, ComplianceHistoryResponseDTO, MarksmanGradingQualificationDTO } from '../../services/apiClient';
import { FileViewer } from '../../components/common/FileViewer';
import DocumentCard from '../../components/common/DocumentCard';
import HtmlCertificatePreview from '../../components/common/HtmlCertificatePreview';
import { MemberCategoryConstants } from '../../constants/MemberCategoryConstants';
import { CourseTypeConstants } from '../../constants/CourseTypeConstants';
import { EndorsementStatusConstants } from '../../constants/EndorsementStatusConstants';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import List from './memberAdditionalDocument/List';
import { useLocation } from 'react-router-dom';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';

const Documents: React.FC = () => {
    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Documents', href: '#' },
    ];

    const location = useLocation();
    const memberId = getMemberId();
    const [isMobile, setIsMobile] = useState(false);
    const { getById, getCertificate: getMemberCertificate, getHtmlCertificate: getMemberHtmlCertificate, getMemberCard, getHtmlMemberCard, getReachedMilestones, getAnniversaryCertificate, getHtmlAnniversaryCertificate } = useMemberApi();
    const { search: searchEndorsements, getCertificate, getHtmlCertificate } = useMemberEndorsementApi();
    const { generateCertificate: generateDedicatedStatusCertificate, generateHtmlCertificate: generateDedicatedStatusHtmlCertificate } = useDedicatedStatusApi();
    const { search: searchActivities, getReport, getSportShootingListPdf, getSportShootingListHtml, getActivityReportHtml, getComplianceActivities, getAllComplianceActivitiesHistory } = useActivityApi();
    const { getMemberQualifications, generateCertificate: generateMarksmanCertificate } = useMarksmanGradingApi();
    const { search: searchCourses } = useCourseApi();
    const { search: searchRpls } = useRplApi();
    const { getDistinctYears: getCompetitionEntryYears, generateParticipationCertificate, generateHtmlParticipationCertificate } = useCompetitionEntryApi();
    const [member, setMember] = useState<MemberDTO | null>(null);
    const [approvedEndorsements, setApprovedEndorsements] = useState<MemberEndorsementDTO[]>([]);
    const [hasApprovedActivities, setHasApprovedActivities] = useState(false);
    const [viewingFile, setViewingFile] = useState<{ name: string; url: string; } | null>(null);
    const [certificateHtml, setCertificateHtml] = useState<{ [key: string]: string }>({});
    const [loadingCertificates, setLoadingCertificates] = useState<{ [key: string]: boolean }>({});
    const [dedicatedStatusHtml, setDedicatedStatusHtml] = useState<{ [key: string]: string }>({});
    const [loadingDedicatedStatus, setLoadingDedicatedStatus] = useState<{ [key: string]: boolean }>({});
    const [memberCertificateHtml, setMemberCertificateHtml] = useState<string | null>(null);
    const [loadingMemberCertificate, setLoadingMemberCertificate] = useState(false);
    const [memberCardHtml, setMemberCardHtml] = useState<string | null>(null);
    const [loadingMemberCard, setLoadingMemberCard] = useState(false);
    const [sportShootingListHtml, setSportShootingListHtml] = useState<string | null>(null);
    const [loadingSportShootingList, setLoadingSportShootingList] = useState(false);
    const [activityReportHtml, setActivityReportHtml] = useState<string | null>(null);
    const [loadingActivityReport, setLoadingActivityReport] = useState(false);
    const [complianceItems, setComplianceItems] = useState<ActivityDTO[]>([]);
    const [threeYearHistory, setThreeYearHistory] = useState<ComplianceHistoryResponseDTO | null>(null);
    const [marksmanQualifications, setMarksmanQualifications] = useState<MarksmanGradingQualificationDTO | null>(null);
    const [loadingQualifications, setLoadingQualifications] = useState(false);
    const [anniversaryMilestones, setAnniversaryMilestones] = useState<number[]>([]);
    const [loadingAnniversaryMilestones, setLoadingAnniversaryMilestones] = useState(false);
    const [cardDownloadLoading, setCardDownloadLoading] = useState<{ [key: string]: boolean }>({});
    const [competitionEntryYears, setCompetitionEntryYears] = useState<number[]>([]);
    const [loadingCompetitionEntryYears, setLoadingCompetitionEntryYears] = useState(false);
    const [competitionCertificateHtml, setCompetitionCertificateHtml] = useState<{ [key: string]: string }>({});
    const [loadingCompetitionCertificate, setLoadingCompetitionCertificate] = useState<{ [key: string]: boolean }>({});

    // Read hash from URL to determine initial active tab
    const [activeTab] = useState(() => {
        const hash = location.hash.replace('#', '');
        const validTabs = ['membership', 'certificates', 'endorsements', 'competitions', 'gradings', 'anniversary', 'additional'];
        return validTabs.includes(hash) ? hash : 'membership';
    });

    const cardKeys = {
        memberCard: 'membership-member-card',
        memberCertificate: 'membership-member-certificate',
        sportShootingList: 'membership-sport-shooting-list',
        activityReport: 'membership-activity-report',
        dedicatedHunt: 'certificate-dedicated-hunt',
        dedicatedSport: 'certificate-dedicated-sport',
    };

    const getEndorsementCardKey = (endorsementId: string) => `endorsement-${endorsementId}`;
    const getGradingCardKey = (category: any) => `grading-${category.gradingCategoryId}-${category.year}`;

    const setDownloadLoading = (key: string, value: boolean) => {
        setCardDownloadLoading(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await getById(memberId!);
                setMember(response?.data || null);
            } catch (error) {
                console.error('Error fetching Member Firearms', error);
            }
        };

        const fetchApprovedEndorsements = async () => {
            try {
                const response = await searchEndorsements({
                    filters: {
                        isDeleted: false,
                    },
                    page: 1,
                    pageSize: 100,
                    sortBy: 'CreatedUtc',
                    sortDescending: true,
                });

                const approvedResults = response?.data?.results?.filter(
                    (endorsement: MemberEndorsementDTO) =>
                        endorsement.statusId === EndorsementStatusConstants.APPROVED ||
                        endorsement.statusId === EndorsementStatusConstants.APPROVED_WITH_FEEDBACK
                ) || [];

                setApprovedEndorsements(approvedResults);
            } catch (error) {
                console.error('Error fetching approved endorsements:', error);
            }
        };

        const checkApprovedActivities = async () => {
            try {
                const response = await searchActivities({
                    filters: {
                        isDeleted: false,
                    },
                    page: 1,
                    pageSize: 100,
                    sortBy: 'CreatedUtc',
                    sortDescending: true,
                });

                // Check if any activities have approved status
                const hasApproved = response?.data?.results?.some(
                    (activity: ActivityDTO) =>
                        activity.activityStatus?.title?.toLowerCase().includes('approved')
                ) || false;

                setHasApprovedActivities(hasApproved);
            } catch (error) {
                console.error('Error fetching activities:', error);
            }
        };

        const fetchComplianceActivities = async () => {
            try {
                const response = await getComplianceActivities();
                setComplianceItems(response?.data || []);
            } catch (error) {
                console.error('Error fetching Compliance Activities', error);
            }
        };

        const fetchThreeYearHistory = async () => {
            try {

                // Retrieve 3 years of compliance history
                // Offset starts at 0 for current year
                var complianceYearsToFetch = 2;
                const response = await getAllComplianceActivitiesHistory(complianceYearsToFetch);
                setThreeYearHistory(response?.data || null);
            } catch (error) {
                console.error('Error fetching 3-year compliance history', error);
            }
        };

        const fetchMarksmanQualifications = async () => {
            if (!memberId) return;
            setLoadingQualifications(true);
            try {
                const response = await getMemberQualifications(memberId);
                setMarksmanQualifications(response?.data || null);
            } catch (error) {
                console.error('Error fetching marksman qualifications', error);
            } finally {
                setLoadingQualifications(false);
            }
        };

        const fetchAnniversaryMilestones = async () => {
            if (!memberId) return;
            setLoadingAnniversaryMilestones(true);
            try {
                const milestones = await getReachedMilestones(memberId);
                setAnniversaryMilestones(milestones || []);
            } catch (error) {
                console.error('Error fetching anniversary milestones', error);
            } finally {
                setLoadingAnniversaryMilestones(false);
            }
        };

        const fetchCompetitionEntryYears = async () => {
            if (!memberId) return;
            setLoadingCompetitionEntryYears(true);
            try {
                const years = await getCompetitionEntryYears();
                setCompetitionEntryYears(years);
            } catch (error) {
                console.error('Error fetching competition entry years', error);
            } finally {
                setLoadingCompetitionEntryYears(false);
            }
        };

        fetchMember();
        fetchApprovedEndorsements();
        checkApprovedActivities();
        fetchComplianceActivities();
        fetchThreeYearHistory();
        fetchMarksmanQualifications();
        fetchAnniversaryMilestones();
        fetchCompetitionEntryYears();
    }, [memberId]);

    // Detect mobile view
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md breakpoint
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch HTML certificate preview for an endorsement
    const fetchEndorsementHtmlCertificate = async (endorsementId: string) => {
        if (certificateHtml[endorsementId] || loadingCertificates[endorsementId]) {
            return; // Already loaded or loading
        }

        setLoadingCertificates(prev => ({ ...prev, [endorsementId]: true }));
        try {
            const htmlContent = await getHtmlCertificate(endorsementId);
            setCertificateHtml(prev => ({ ...prev, [endorsementId]: htmlContent }));
        } catch (error) {
            console.error('Error fetching HTML certificate:', error);
            setCertificateHtml(prev => ({ ...prev, [endorsementId]: 'Error loading certificate preview' }));
        } finally {
            setLoadingCertificates(prev => ({ ...prev, [endorsementId]: false }));
        }
    };

    // Fetch HTML certificate preview for dedicated status
    const fetchDedicatedStatusHtmlCertificate = async (certificateType: 'HUNT' | 'SPORT') => {
        const courseTypeId = certificateType === 'HUNT'
            ? CourseTypeConstants.DEDICATED_HUNTER
            : CourseTypeConstants.DEDICATED_SPORTS_PERSON;

        if (dedicatedStatusHtml[certificateType] || loadingDedicatedStatus[certificateType]) {
            return; // Already loaded or loading
        }

        setLoadingDedicatedStatus(prev => ({ ...prev, [certificateType]: true }));
        try {
            // Find the member's course or RPL record for the specific type
            const courseResponse = await searchCourses({
                filters: {
                    memberId,
                    courseTypeId,
                    isDeleted: false,
                },
                page: 1,
                pageSize: 1,
                sortBy: 'CreatedUtc',
                sortDescending: true,
            });

            let itemId: string | undefined = courseResponse?.data?.results?.[0]?.id;

            // If no course found, try RPL
            if (!itemId) {
                const rplResponse = await searchRpls({
                    filters: {
                        memberId,
                        courseTypeId,
                        isDeleted: false,
                    },
                    page: 1,
                    pageSize: 1,
                    sortBy: 'CreatedUtc',
                    sortDescending: true,
                });
                itemId = rplResponse?.data?.results?.[0]?.id;
            }

            // If no course or RPL found, use memberId as fallback
            // This allows certificate generation for members awarded Dedicated Status without course/RPL
            const finalItemId = itemId || memberId!;

            const htmlContent = await generateDedicatedStatusHtmlCertificate(courseTypeId, finalItemId);
            setDedicatedStatusHtml(prev => ({ ...prev, [certificateType]: htmlContent }));
        } catch (error) {
            console.error('Error fetching dedicated status HTML certificate:', error);
            setDedicatedStatusHtml(prev => ({ ...prev, [certificateType]: 'Error loading certificate preview' }));
        } finally {
            setLoadingDedicatedStatus(prev => ({ ...prev, [certificateType]: false }));
        }
    };

    const handleDownloadEndorsementCertificate = async (endorsement: MemberEndorsementDTO) => {
        const cardKey = getEndorsementCardKey(endorsement.id!);
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const certificateUrl = await getCertificate(endorsement.id!);
            const filename = `Endorsement-Certificate-${endorsement.licenceType?.title || 'Unknown'}(${endorsement.id}).pdf`;
            setViewingFile({
                name: filename,
                url: certificateUrl,
            });
        } catch (error) {
            console.error('Error generating certificate:', error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const handleDownloadDedicatedCertificate = async (certificateType: 'HUNT' | 'SPORT') => {
        const cardKey = certificateType === 'HUNT' ? cardKeys.dedicatedHunt : cardKeys.dedicatedSport;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const courseTypeId = certificateType === 'HUNT'
                ? CourseTypeConstants.DEDICATED_HUNTER
                : CourseTypeConstants.DEDICATED_SPORTS_PERSON;

            // Find the member's course or RPL record for the specific type
            const courseResponse = await searchCourses({
                filters: {
                    memberId,
                    courseTypeId,
                    isDeleted: false,
                },
                page: 1,
                pageSize: 1,
                sortBy: 'CreatedUtc',
                sortDescending: true,
            });

            let itemId: string | undefined = courseResponse?.data?.results?.[0]?.id;

            // If no course found, try RPL
            if (!itemId) {
                const rplResponse = await searchRpls({
                    filters: {
                        memberId,
                        courseTypeId,
                        isDeleted: false,
                    },
                    page: 1,
                    pageSize: 1,
                    sortBy: 'CreatedUtc',
                    sortDescending: true,
                });
                itemId = rplResponse?.data?.results?.[0]?.id;
            }

            // If no course or RPL found, use memberId as fallback
            // This allows certificate generation for members awarded Dedicated Status without course/RPL
            const finalItemId = itemId || memberId!;

            const certificateUrl = await generateDedicatedStatusCertificate(courseTypeId, finalItemId);
            const filename = `Dedicated-${certificateType}-Certificate(${memberId}).pdf`;

            setViewingFile({
                name: filename,
                url: certificateUrl,
            });
        } catch (error) {
            console.error('Error generating certificate:', error);
            alert(`Error downloading ${certificateType} certificate. Please try again.`);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const handleActivityReport = async () => {
        const cardKey = cardKeys.activityReport;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const response = await getReport(memberId!);
            if (response?.success && response.data) {
                setViewingFile({
                    name: `Activity-Report.pdf`,
                    url: response.data,
                });
            }
        } catch (error) {
            console.error('Error generating activity report:', error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    // Fetch HTML certificate preview for member
    const fetchMemberHtmlCertificate = async () => {
        if (memberCertificateHtml || loadingMemberCertificate) {
            return; // Already loaded or loading
        }

        setLoadingMemberCertificate(true);
        try {
            const htmlContent = await getMemberHtmlCertificate(memberId!);
            setMemberCertificateHtml(htmlContent);
        } catch (error) {
            console.error('Error fetching member HTML certificate:', error);
            setMemberCertificateHtml('Error loading certificate preview');
        } finally {
            setLoadingMemberCertificate(false);
        }
    };

    // Fetch HTML certificate preview for sport shooting list
    const fetchSportShootingListHtml = async () => {
        if (sportShootingListHtml || loadingSportShootingList) {
            return; // Already loaded or loading
        }

        setLoadingSportShootingList(true);
        try {
            const htmlContent = await getSportShootingListHtml(memberId!);
            setSportShootingListHtml(htmlContent);
        } catch (error) {
            console.error('Error fetching sport shooting list HTML:', error);
            setSportShootingListHtml('Error loading certificate preview');
        } finally {
            setLoadingSportShootingList(false);
        }
    };

    // Fetch HTML preview for member card
    const fetchMemberCardHtml = async () => {
        if (memberCardHtml || loadingMemberCard) {
            return; // Already loaded or loading
        }

        setLoadingMemberCard(true);
        try {
            const htmlContent = await getHtmlMemberCard(memberId!);
            setMemberCardHtml(htmlContent);
        } catch (error) {
            console.error('Error fetching member card HTML:', error);
            setMemberCardHtml('Error loading member card preview');
        } finally {
            setLoadingMemberCard(false);
        }
    };

    // Fetch HTML preview for activity report
    const fetchActivityReportHtml = async () => {
        if (activityReportHtml || loadingActivityReport) {
            return; // Already loaded or loading
        }

        setLoadingActivityReport(true);
        try {
            const htmlContent = await getActivityReportHtml(memberId!);
            setActivityReportHtml(htmlContent);
        } catch (error) {
            console.error('Error fetching activity report HTML:', error);
            setActivityReportHtml('Error loading activity report preview');
        } finally {
            setLoadingActivityReport(false);
        }
    };

    const handleDownloadMemberCertificate = async () => {
        const cardKey = cardKeys.memberCertificate;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const certificateUrl = await getMemberCertificate(memberId!);
            const filename = `Proof-of-Membership-${member?.firstName || 'Member'}-${member?.lastName || ''}(${memberId}).pdf`;
            setViewingFile({
                name: filename,
                url: certificateUrl,
            });
        } catch (error) {
            console.error('Error generating member certificate:', error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const handleDownloadSportShootingList = async () => {
        const cardKey = cardKeys.sportShootingList;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const certificateUrl = await getSportShootingListPdf(memberId!);
            const filename = `Sport-Shooting-List-${member?.firstName || 'Member'}-${member?.lastName || ''}(${memberId}).pdf`;
            setViewingFile({
                name: filename,
                url: certificateUrl,
            });
        } catch (error) {
            console.error('Error generating sport shooting list:', error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const handleDownloadMemberCard = async () => {
        const cardKey = cardKeys.memberCard;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const cardUrl = await getMemberCard(memberId!);
            const filename = `Member-Card-${member?.firstName || 'Member'}-${member?.lastName || ''}(${memberId}).pdf`;
            setViewingFile({
                name: filename,
                url: cardUrl,
            });
        } catch (error) {
            console.error('Error generating member card:', error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const handleDownloadMarksmanCertificate = async (category: any) => {
        const cardKey = getGradingCardKey(category);
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const response = await generateMarksmanCertificate(memberId!, category);

            if (response?.success && response.data) {
                const filename = `Marksman-Certificate-${category.gradingTypeName}-${category.categoryName}-${category.year}.pdf`;
                setViewingFile({
                    name: filename,
                    url: response.data,
                });
            }
        } catch (error: any) {
            console.error('Error generating marksman grading certificate:', error);
            if (error?.message?.includes('not yet implemented')) {
                alert(`Certificate generation is not yet implemented. This feature will be available soon.`);
            } else {
                alert(`Error generating certificate: ${error?.message || 'Unknown error'}`);
            }
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const getTierName = (years: number): string => {
        switch (years) {
            case 10:
                return 'Bronze';
            case 20:
                return 'Silver';
            case 25:
                return 'Gold';
            case 30:
                return 'Platinum';
            default:
                return 'Standard';
        }
    };

    const handleDownloadAnniversaryCertificate = async (years: number) => {
        const cardKey = `anniversary-${years}`;
        if (cardDownloadLoading[cardKey]) return;
        setDownloadLoading(cardKey, true);
        try {
            const certificateUrl = await getAnniversaryCertificate(memberId!, years);
            const filename = `Anniversary-Certificate-${years}-Years-${member?.firstName || 'Member'}-${member?.lastName || ''}(${memberId}).pdf`;
            setViewingFile({
                name: filename,
                url: certificateUrl,
            });
        } catch (error) {
            console.error(`Error generating ${years}-year anniversary certificate:`, error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const getCompetitionCertificateCardKey = (year: number) => `competition-participation-${year}`;

    // Fetch HTML certificate preview for a competition participation year
    const fetchCompetitionCertificateHtml = async (year: number) => {
        const cardKey = getCompetitionCertificateCardKey(year);
        if (competitionCertificateHtml[cardKey] || loadingCompetitionCertificate[cardKey] || !memberId) {
            return; // Already loaded or loading
        }

        setLoadingCompetitionCertificate(prev => ({ ...prev, [cardKey]: true }));
        try {
            const htmlContent = await generateHtmlParticipationCertificate(memberId, year);
            setCompetitionCertificateHtml(prev => ({ ...prev, [cardKey]: htmlContent || 'Error loading certificate preview' }));
        } catch (error) {
            console.error('Error fetching HTML competition participation certificate:', error);
            setCompetitionCertificateHtml(prev => ({ ...prev, [cardKey]: 'Error loading certificate preview' }));
        } finally {
            setLoadingCompetitionCertificate(prev => ({ ...prev, [cardKey]: false }));
        }
    };

    const handleDownloadCompetitionCertificate = async (year: number) => {
        const cardKey = getCompetitionCertificateCardKey(year);
        if (cardDownloadLoading[cardKey] || !memberId) return;
        setDownloadLoading(cardKey, true);
        try {
            const certificateUrl = await generateParticipationCertificate(memberId, year);
            const filename = `Competition-Participation-Certificate-${year}-${member?.firstName || 'Member'}-${member?.lastName || ''}(${memberId}).pdf`;
            setViewingFile({
                name: filename,
                url: certificateUrl!,
            });
        } catch (error) {
            console.error(`Error generating ${year} competition participation certificate:`, error);
        } finally {
            setDownloadLoading(cardKey, false);
        }
    };

    const renderMembershipTab = () => (
        <div>
            <h2 className="text-xl font-semibold mb-4">Membership Documents</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Member Card */}
                <DocumentCard
                    title="Member Card"
                    description="Digital membership card for identification"
                    rows={[]}
                    onDownload={handleDownloadMemberCard}
                    isLoading={loadingMemberCard || !!cardDownloadLoading[cardKeys.memberCard]}
                    preview={(() => {
                        if (isMobile) return null;

                        // Fetch preview when needed
                        if (!memberCardHtml && !loadingMemberCard) {
                            fetchMemberCardHtml();
                        }

                        return (
                            <HtmlCertificatePreview
                                htmlContent={memberCardHtml || null}
                                loading={loadingMemberCard || false}
                                error={memberCardHtml === 'Error loading member card preview' ? 'Error loading member card preview' : null}
                                height="450px"
                                width="435px"
                                scale={0.4}
                                maxWidth={1080}
                            />
                        );
                    })()}
                />

                {/* Proof of Membership Card */}
                <DocumentCard
                    title="Proof of Membership"
                    description="Certificate for proof of paid-up membership"
                    rows={[]}
                    onDownload={handleDownloadMemberCertificate}
                    isLoading={loadingMemberCertificate || !!cardDownloadLoading[cardKeys.memberCertificate]}
                    preview={(() => {
                        if (isMobile) return null;

                        // Fetch preview when needed
                        if (!memberCertificateHtml && !loadingMemberCertificate) {
                            fetchMemberHtmlCertificate();
                        }

                        return (
                            <HtmlCertificatePreview
                                htmlContent={memberCertificateHtml || null}
                                loading={loadingMemberCertificate || false}
                                error={memberCertificateHtml === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                height="450px"
                                width="435px"
                                scale={0.4}
                                maxWidth={1080}
                            />
                        );
                    })()}
                />

                {/* Sport Shooting List Card */}
                <DocumentCard
                    title="Sport Shooting List"
                    description="Certificate for additional info for SAPS application(s)"
                    rows={[]}
                    onDownload={handleDownloadSportShootingList}
                    isLoading={loadingSportShootingList || !!cardDownloadLoading[cardKeys.sportShootingList]}
                    preview={(() => {
                        if (isMobile) return null;

                        // Fetch preview when needed
                        if (!sportShootingListHtml && !loadingSportShootingList) {
                            fetchSportShootingListHtml();
                        }

                        return (
                            <HtmlCertificatePreview
                                htmlContent={sportShootingListHtml || null}
                                loading={loadingSportShootingList || false}
                                error={sportShootingListHtml === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                height="450px"
                                width="435px"
                                scale={0.4}
                                maxWidth={1080}
                            />
                        );
                    })()}
                />

                {/* Activity Report Card */}
                {hasApprovedActivities && (
                    <DocumentCard
                        title="Activities and Scores Report"
                        description="Register of annual Hunting and/or sport shooting activities "
                        rows={[]}
                        onDownload={handleActivityReport}
                        isLoading={loadingActivityReport || !!cardDownloadLoading[cardKeys.activityReport]}
                        preview={(() => {
                            if (isMobile) return null;

                            // Fetch preview when needed
                            if (!activityReportHtml && !loadingActivityReport) {
                                fetchActivityReportHtml();
                            }

                            return (
                                <HtmlCertificatePreview
                                    htmlContent={activityReportHtml || null}
                                    loading={loadingActivityReport || false}
                                    error={activityReportHtml === 'Error loading activity report preview' ? 'Error loading activity report preview' : null}
                                    height="450px"
                                    width="435px"
                                    scale={0.4}
                                    maxWidth={1080}
                                />
                            );
                        })()}
                    />
                )}
            </div>
        </div>
    );

    const renderCertificatesTab = () => {
        const memberCategoryId = member?.memberCategoryId;

        // Calculate current status from the first year in 3-year history (current year)
        const currentStatus = useMemo(() => {
            if (!threeYearHistory?.complianceYears || threeYearHistory.complianceYears.length === 0) {
                return { hunter: undefined, sport: undefined };
            }

            const currentYear = threeYearHistory.complianceYears[0]; // First year is current year
            const status: { hunter?: string; sport?: string } = {};

            // Check Hunt status
            if (currentYear.certificateStatus?.hunter) {
                if (currentYear.certificateStatus.hunter === 'not-needed') {
                    status.hunter = 'Compliant';
                } else if (currentYear.certificateStatus.hunter === 'needed') {
                    const huntActivities = currentYear.activities?.filter((a: any) => a.activityTypeId === ActivityTypeConstants.HUNT_RELATED) || [];
                    status.hunter = huntActivities.length >= 2 ? 'Compliant' : 'Not compliant';
                }
            }

            // Check Sport status
            if (currentYear.certificateStatus?.sport) {
                if (currentYear.certificateStatus.sport === 'not-needed') {
                    status.sport = 'Compliant';
                } else if (currentYear.certificateStatus.sport === 'needed') {
                    const sportActivities = currentYear.activities?.filter((a: any) => a.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING) || [];
                    status.sport = sportActivities.length >= 2 ? 'Compliant' : 'Not compliant';
                }
            }

            return status;
        }, [threeYearHistory]);

        // Check if member has been non-compliant for 3 consecutive years
        const threeYearNonCompliance = useMemo(() => {
            if (!threeYearHistory?.complianceYears || threeYearHistory.complianceYears.length < 3) {
                return { hunter: false, sport: false };
            }

            const years = threeYearHistory.complianceYears.slice(0, 3); // Get first 3 years
            const result = { hunter: false, sport: false };

            // Check Hunt non-compliance for all 3 years
            const allHuntYearsNonCompliant = years.every((year: any) => {
                if (!year.certificateStatus?.hunter || year.certificateStatus.hunter !== 'needed') {
                    return false; // If cert doesn't exist or not needed, don't count as non-compliant
                }
                const huntActivities = year.activities?.filter((a: any) => a.activityTypeId === ActivityTypeConstants.HUNT_RELATED) || [];
                return huntActivities.length < 2;
            });
            result.hunter = allHuntYearsNonCompliant && years[0].certificateStatus?.hunter === 'needed';

            // Check Sport non-compliance for all 3 years
            const allSportYearsNonCompliant = years.every((year: any) => {
                if (!year.certificateStatus?.sport || year.certificateStatus.sport !== 'needed') {
                    return false; // If cert doesn't exist or not needed, don't count as non-compliant
                }
                const sportActivities = year.activities?.filter((a: any) => a.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING) || [];
                return sportActivities.length < 2;
            });
            result.sport = allSportYearsNonCompliant && years[0].certificateStatus?.sport === 'needed';

            return result;
        }, [threeYearHistory]);

        // Determine which certificates to show based on member category
        const showHuntCertificate =
            ((memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER ||
                memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON) &&
                member?.dateCertificateHunter != null) ||
            (memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON &&
                member?.dateCertificateHunter != null);

        const showSportCertificate =
            ((memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON ||
                memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER) &&
                member?.dateCertificateSport != null) ||
            (memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON &&
                member?.dateCertificateSport != null);

        if (!showHuntCertificate && !showSportCertificate) {
            return (
                <div>
                    <h2 className="text-xl font-semibold mb-4">Certificates</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        No certificates available. Certificates are only available for dedicated status members.
                    </p>
                </div>
            );
        }

        return (
            <div>
                <h2 className="text-xl font-semibold mb-4">Certificates</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {showHuntCertificate && (
                        <DocumentCard
                            title="Dedicated Hunter Certificate"
                            description="Certificate for Dedicated Hunting status"
                            rows={[]}
                            onDownload={() => handleDownloadDedicatedCertificate('HUNT')}
                            hideDownload={threeYearNonCompliance.hunter}
                            isLoading={(loadingDedicatedStatus['HUNT'] || !!cardDownloadLoading[cardKeys.dedicatedHunt]) && !threeYearNonCompliance.hunter}
                            preview={(() => {
                                // Show warning message if non-compliant for 3 years
                                if (threeYearNonCompliance.hunter) {
                                    return (
                                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
                                            <div className="flex items-start mb-3">
                                                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300">Dedicated hunt status not up to date</h3>
                                                    <p className="text-sm text-yellow-800 dark:text-yellow-400 mt-1">
                                                        You can download your dedicated status certificate once you've logged your two activities for dedicated hunt status.
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-yellow-800 dark:text-yellow-400 pl-8">
                                                See <a href="https://natshoot.co.za/dedicated-status/#maintain-dedicated-hunter-and-or-sport-person-status" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-900 dark:hover:text-yellow-300">this link</a> for more information.
                                            </p>
                                        </div>
                                    );
                                }

                                if (isMobile) return null;

                                // Fetch preview when needed
                                if (!dedicatedStatusHtml['HUNT'] && !loadingDedicatedStatus['HUNT']) {
                                    fetchDedicatedStatusHtmlCertificate('HUNT');
                                }

                                return (
                                    <HtmlCertificatePreview
                                        htmlContent={dedicatedStatusHtml['HUNT'] || null}
                                        loading={loadingDedicatedStatus['HUNT'] || false}
                                        error={dedicatedStatusHtml['HUNT'] === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                        height="450px"
                                        width="435px"
                                        scale={0.4}
                                        maxWidth={1080}
                                    />
                                );
                            })()}
                        />
                    )}

                    {showSportCertificate && (
                        <DocumentCard
                            title="Dedicated Sport Certificate"
                            description="Certificate for Dedicated Sport-Shooting status"
                            rows={[]}
                            onDownload={() => handleDownloadDedicatedCertificate('SPORT')}
                            hideDownload={threeYearNonCompliance.sport}
                            isLoading={(loadingDedicatedStatus['SPORT'] || !!cardDownloadLoading[cardKeys.dedicatedSport]) && !threeYearNonCompliance.sport}
                            preview={(() => {
                                // Show warning message if non-compliant for 3 years
                                if (threeYearNonCompliance.sport) {
                                    return (
                                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
                                            <div className="flex items-start mb-3">
                                                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300">Dedicated sport status not up to date</h3>
                                                    <p className="text-sm text-yellow-800 dark:text-yellow-400 mt-1">
                                                        You can download your dedicated status certificate once you've logged your two activities for dedicated sport status.
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-yellow-800 dark:text-yellow-400 pl-8">
                                                See <a href="https://natshoot.co.za/dedicated-status/#maintain-dedicated-hunter-and-or-sport-person-status" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-900 dark:hover:text-yellow-300">this link</a> for more information.
                                            </p>
                                        </div>
                                    );
                                }

                                if (isMobile) return null;

                                // Fetch preview when needed
                                if (!dedicatedStatusHtml['SPORT'] && !loadingDedicatedStatus['SPORT']) {
                                    fetchDedicatedStatusHtmlCertificate('SPORT');
                                }

                                return (
                                    <HtmlCertificatePreview
                                        htmlContent={dedicatedStatusHtml['SPORT'] || null}
                                        loading={loadingDedicatedStatus['SPORT'] || false}
                                        error={dedicatedStatusHtml['SPORT'] === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                        height="450px"
                                        width="435px"
                                        scale={0.4}
                                        maxWidth={1080}
                                    />
                                );
                            })()}
                        />
                    )}
                </div>
            </div>
        );
    };

    const renderEndorsementsTab = () => (
        <div>
            <h2 className="text-xl font-semibold mb-4">Endorsements</h2>
            {approvedEndorsements.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No approved endorsements found.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {approvedEndorsements.map((endorsement) => {
                        const rows = [];

                        if (endorsement.firearm) {
                            const makeTitle = endorsement.firearm.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? endorsement.firearm.firearmMakeOther || 'Other Make' : endorsement.firearm.firearmMake?.title || 'N/A';
                            const caliberTitle = endorsement.firearm.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? endorsement.firearm.caliberOther || 'Other Caliber' : endorsement.firearm.caliber?.title || 'N/A';
                            rows.push({
                                label: 'Firearm',
                                value: `${endorsement.firearm?.firearmType?.title}, ${makeTitle}, ${caliberTitle}` || 'N/A'
                            });
                        }

                        rows.push({
                            label: 'Applied',
                            value: endorsement.createdUtc
                                ? new Date(endorsement.createdUtc).toLocaleDateString()
                                : 'N/A'
                        });

                        // Fetch preview when needed
                        const getPreview = () => {
                            if (isMobile) return null;

                            if (!certificateHtml[endorsement.id!] && !loadingCertificates[endorsement.id!]) {
                                fetchEndorsementHtmlCertificate(endorsement.id!);
                            }

                            return (
                                <HtmlCertificatePreview
                                    htmlContent={certificateHtml[endorsement.id!] || null}
                                    loading={loadingCertificates[endorsement.id!] || false}
                                    error={certificateHtml[endorsement.id!] === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                    height="450px"
                                    width="435px"
                                    scale={0.4}
                                    maxWidth={1080}
                                />
                            );
                        };

                        return (
                            <DocumentCard
                                key={endorsement.id}
                                title="Endorsement of Motivation Certificate"
                                rows={rows}
                                onDownload={() => handleDownloadEndorsementCertificate(endorsement)}
                                isLoading={!!loadingCertificates[endorsement.id!] || !!cardDownloadLoading[getEndorsementCardKey(endorsement.id!)]}
                                preview={getPreview()}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderCompetitionsTab = () => {
        if (loadingCompetitionEntryYears) {
            return (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading competition certificates...</span>
                </div>
            );
        }

        if (competitionEntryYears.length === 0) {
            return (
                <div>
                    <h2 className="text-xl font-semibold mb-4">Competitions</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        No competition participation certificates available. Certificates appear here once you have entered a competition.
                    </p>
                </div>
            );
        }

        return (
            <div>
                <h2 className="text-xl font-semibold mb-4">Competitions</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Download your certificate of participation for each year you entered the National Postal Target Shooting Competition.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {competitionEntryYears.map((year) => {
                        const cardKey = getCompetitionCertificateCardKey(year);

                        return (
                            <DocumentCard
                                key={cardKey}
                                title={`${year} Certificate of Participation`}
                                description="National Postal Target Shooting Competition"
                                rows={[]}
                                onDownload={() => handleDownloadCompetitionCertificate(year)}
                                isLoading={!!cardDownloadLoading[cardKey]}
                                preview={(() => {
                                    if (isMobile) return null;

                                    if (!competitionCertificateHtml[cardKey] && !loadingCompetitionCertificate[cardKey]) {
                                        fetchCompetitionCertificateHtml(year);
                                    }

                                    return (
                                        <HtmlCertificatePreview
                                            htmlContent={competitionCertificateHtml[cardKey] || null}
                                            loading={loadingCompetitionCertificate[cardKey] || false}
                                            error={competitionCertificateHtml[cardKey] === 'Error loading certificate preview' ? 'Error loading certificate preview' : null}
                                            height="450px"
                                            width="435px"
                                            scale={0.4}
                                            maxWidth={1080}
                                        />
                                    );
                                })()}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderGradingsTab = () => {
        const yearQualifications = marksmanQualifications?.yearQualifications ?? [];

        if (loadingQualifications) {
            return (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading qualifications...</span>
                </div>
            );
        }

        if (!marksmanQualifications || yearQualifications.length === 0) {
            return (
                <div>
                    <h2 className="text-xl font-semibold mb-4">Marksman Gradings</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        No marksman grading qualifications found. Complete exercises and submit scores to qualify for grading certificates.
                    </p>
                </div>
            );
        }

        return (
            <div>
                <h2 className="text-xl font-semibold mb-4">Marksman Gradings</h2>

                {yearQualifications.map((yearQual) => (
                    <div key={yearQual.year} className="mb-8">
                        <h3 className="text-lg font-semibold mb-4 dark:text-blue-400">
                            {yearQual.year} Qualifications
                        </h3>

                        {yearQual.gradingTypeQualifications?.map((gradingType) => (
                            <div key={gradingType.gradingTypeId} className="mb-6">
                                <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">
                                    {gradingType.gradingTypeName}
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {gradingType.qualifiedCategories?.map((category) => {

                                        const rows = [
                                            {
                                                label: 'Next Level',
                                                value: category.gradingLevel === 'High Master'
                                                    ? '🏆 Highest Level Achieved'
                                                    : (category.nextGradingLevel && category.nextGradingLevelThreshold)
                                                        ? `${category.nextGradingLevel} (${category.nextGradingLevelThreshold.toFixed(0)}% required)`
                                                        : 'N/A'
                                            },
                                            {
                                                label: 'Scores Submitted',
                                                value: category.totalScoresSubmitted! > 6
                                                    ? `Top 6 out of ${category.totalScoresSubmitted?.toString()}`
                                                    : category.totalScoresSubmitted?.toString() || 'N/A'
                                            },
                                            {
                                                label: 'Average Percentage',
                                                value: category.averageScore ? `${category.averageScore.toFixed(2)}%` : 'N/A'
                                            },
                                            {
                                                label: 'Date Range',
                                                value: category.firstScoreDate && category.lastScoreDate
                                                    ? `${new Date(category.firstScoreDate).toLocaleDateString()} - ${new Date(category.lastScoreDate).toLocaleDateString()}`
                                                    : 'N/A'
                                            },

                                        ];

                                        // Only show download button if average score is 80% or higher
                                        const canDownload = category.averageScore && category.averageScore >= 80;

                                        return (
                                            <DocumentCard
                                                key={category.gradingCategoryId}
                                                title={category.gradingLevelDescription || `Qualified for ${gradingType.gradingTypeName}`}
                                                description={category.categoryName}
                                                rows={rows}
                                                onDownload={canDownload ? () => handleDownloadMarksmanCertificate(category) : undefined}
                                                hideDownload={!canDownload}
                                                isLoading={!!cardDownloadLoading[getGradingCardKey(category)]}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const renderAnniversaryTab = () => {
        if (loadingAnniversaryMilestones) {
            return (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading anniversary milestones...</span>
                </div>
            );
        }

        if (!anniversaryMilestones || anniversaryMilestones.length === 0) {
            return (
                <div>
                    <h2 className="text-xl font-semibold mb-4">Anniversary Certificates</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        No anniversary milestones reached yet. Anniversary certificates are available at 10, 20, 25, and 30 years of membership.
                    </p>
                </div>
            );
        }

        // Sort milestones from highest to lowest
        const sortedMilestones = [...anniversaryMilestones].sort((a, b) => b - a);

        return (
            <div>
                <h2 className="text-xl font-semibold mb-4">Anniversary Certificates</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Download your anniversary certificates commemorating your years of support.
                </p>

                <div className="grid grid-cols-2 gap-4">
                    {sortedMilestones.map((years) => {
                        const cardKey = `anniversary-${years}`;
                        const tierName = getTierName(years);

                        const rows = [
                            {
                                label: 'Milestone',
                                value: `${years} Years of Service`
                            },
                            {
                                label: 'Tier',
                                value: tierName
                            },
                            {
                                label: 'Status',
                                value: 'Available for Download'
                            }
                        ];

                        return (
                            <DocumentCard
                                key={cardKey}
                                title={`${years}-Year Anniversary Certificate`}
                                description={`${tierName} tier certificate`}
                                rows={rows}
                                onDownload={() => handleDownloadAnniversaryCertificate(years)}
                                isLoading={!!cardDownloadLoading[cardKey]}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderAdditionalTab = () => (
        <List />
    );

    const tabs = [
        {
            id: 'membership',
            title: 'Membership',
            content: renderMembershipTab()
        },
        {
            id: 'certificates',
            title: 'Dedicated Status Certificates',
            content: renderCertificatesTab()
        },
        {
            id: 'endorsements',
            title: 'Endorsements',
            content: renderEndorsementsTab()
        },
        {
            id: 'competitions',
            title: 'Competitions',
            content: renderCompetitionsTab()
        },
        {
            id: 'gradings',
            title: 'Gradings',
            content: renderGradingsTab()
        },
        {
            id: 'anniversary',
            title: 'Anniversary Certificates',
            content: renderAnniversaryTab()
        },
        ...(!isMobile ? [{
            id: 'additional',
            title: 'Additional/Other Documents',
            content: renderAdditionalTab()
        }] : [])
    ];

    return (
        <AdminLayout
            activeMenuItem="/admin/documents"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Admin', 'User']}
            requiredPermission="Main.Documents.View"
        >
            <div className="h-full">
                <Tab
                    tabs={tabs}
                    defaultActiveTab={activeTab}
                />
            </div>

            {viewingFile && (
                <FileViewer
                    fileName={viewingFile.name}
                    sasUrl={viewingFile.url}
                    onClose={() => setViewingFile(null)}
                />
            )}
        </AdminLayout>
    );
};

export default Documents;
